import React from 'react';

import { Box, BoxProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { StickyStackProvider } from '../StickyStack';
import { useScrollableViewShadow } from './ScrollableViewShadow.hooks';


export type ScrollableViewProps = BoxProps & {
  showScrollShadow?: boolean;
};


export type ScrollableViewContextValue = {
  scrollTo: (scrollToOptions: ScrollToOptions) => void;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
};

export const ScrollableViewContext = React.createContext<ScrollableViewContextValue | null>(null);


const fallbackScrollableView: ScrollableViewContextValue = {
  scrollTo: (opts?: ScrollToOptions) => window.scrollTo(opts),
};


export const useScrollableView = () => {
  const context = React.useContext(ScrollableViewContext);

  return context ?? fallbackScrollableView;
};


const useStyles = makeStyles({ name: 'ScrollableView' })({
  root: {
    flex: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    width: '100%',
    height: '100%',
    overflow: 'hidden auto',
  }
});


export const ScrollableView: React.FC<ScrollableViewProps> = ({ children, className, showScrollShadow = false, ...props }) => {
  const { classes, cx } = useStyles();
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [ scrollContainer, setScrollContainer ] = React.useState<HTMLDivElement | null>(null);
  const { scrollContainerClassName, handleScrollShadowVisible } = useScrollableViewShadow(scrollContainer, showScrollShadow);

  scrollContainerRef.current = scrollContainer;

  const value = React.useMemo(() => ({
    scrollTo: (scrollToOptions: ScrollToOptions) => {
      scrollContainerRef.current?.scrollTo(scrollToOptions);
    },
    scrollContainerRef: scrollContainerRef
  }), []);

  return (
    <ScrollableViewContext.Provider value={value}>
      <StickyStackProvider>
        <Box
          {...props}
          className={cx(
            classes.root,
            className,
            scrollContainerClassName,
          )}
        >
          <div
            ref={setScrollContainer}
            className={classes.content}
            onScroll={handleScrollShadowVisible}
          >
            {children}
          </div>
        </Box>
      </StickyStackProvider>
    </ScrollableViewContext.Provider>
  );
};
